import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG } from "@/data/content";
import { Reveal, PageHero, usePageTitle } from "@/components/Shared";

/* Stacked fullscreen chapters — each formation owns the screen while scrolling */
const BandsPage = () => {
  const { t, lp } = useLang();
  usePageTitle(`${t.nav.bands} — ${ARTIST_NAME}`);

  return (
    <div data-testid="bands-page">
      <PageHero eyebrow={t.bands.eyebrow} title={t.bands.title} img="/images/bands.webp">
        <Reveal delay={0.4}>
          <p className="font-sans text-sm leading-relaxed text-[#A1A1AA]">{t.bands.intro}</p>
        </Reveal>
      </PageHero>

      <div className="relative">
        {t.bands.items.map((b, i) => (
          <div key={b.key} className="sticky top-0 h-screen overflow-hidden">
            <Link
              to={lp(b.path)}
              data-testid={`band-card-${b.key === "band" ? "fullband" : b.key === "events" ? "eventmusic" : b.key}`}
              data-cursor="EXPLORE"
              className="group block h-full w-full"
            >
              <div className="absolute inset-0">
                <img src={IMG[b.img]} alt={b.name} loading="lazy" className="animate-kenburns h-full w-full object-cover" />
                <div className="absolute inset-0 bg-[#0B0B0C]/55 transition-colors duration-700 group-hover:bg-[#0B0B0C]/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-[#0B0B0C]/10 to-[#0B0B0C]/50" />
              </div>

              {/* oversized chapter number */}
              <span className="pointer-events-none absolute -right-6 top-1/2 hidden -translate-y-1/2 select-none font-serif text-[34vw] leading-none text-outline opacity-30 lg:block">
                {b.no}
              </span>

              <span className="absolute left-6 top-24 font-mono text-[10px] tracking-[0.4em] text-[#F9F8F6]/50 sm:left-12 lg:left-20">
                {b.no} / 03
              </span>

              <div className="absolute inset-x-0 bottom-0 px-6 pb-16 sm:px-12 lg:px-20">
                <h2 className="font-serif text-[13vw] font-light leading-[0.85] text-[#F9F8F6] transition-transform duration-700 group-hover:-translate-y-2 sm:text-[9vw] lg:text-[7.5vw]">
                  {b.name}
                </h2>
                <div className="mt-6 flex flex-wrap items-end justify-between gap-6 border-t border-white/15 pt-6">
                  <p className="max-w-md font-sans text-sm leading-relaxed text-[#F9F8F6]/70">{b.desc}</p>
                  <span className="inline-flex items-center gap-3 font-sans text-[11px] font-medium uppercase tracking-[0.3em] text-[#C5A059]">
                    {t.common.explore}
                    <span className="h-px w-8 bg-[#C5A059] transition-all duration-500 group-hover:w-16" />
                    <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BandsPage;
